import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Grid,
  alpha,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  StarOutline,
  WarningAmberOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import { useDistrictCache } from '../../hooks/useDistrictCache';
import { formatSchoolName } from '../../utils/nameFormatter';

// Individual school item with more detail
const SchoolItem = ({ school, type, onClick }) => {
  const isPositive = type === 'bright';
  
  // Get detailed indicators
  const getDetails = () => {
    if (type === 'bright') {
      const indicators = [];
      if (school.enrollment?.status === 'growing' && school.enrollment?.trend) {
        indicators.push(`Enrollment: +${Math.abs(school.enrollment.trend).toFixed(0)}%`);
      }
      if (school.market_share?.status === 'gaining') {
        indicators.push('Market Share: Gaining');
      }
      if (school.enrollment?.projection_status === 'growing') {
        indicators.push('Projections: Growing');
      }
      return indicators.join(' • ');
    } else {
      const indicators = [];
      if (school.enrollment?.status === 'declining' && school.enrollment?.trend) {
        indicators.push(`Enrollment: ${school.enrollment.trend.toFixed(0)}%`);
      }
      if (school.market_share?.status === 'losing') {
        indicators.push('Market Share: Losing');
      }
      if (school.enrollment?.projection_status === 'declining') {
        indicators.push('Projections: Declining');
      }
      return indicators.join(' • ');
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        py: 0.75,
        px: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          bgcolor: isPositive ? alpha('#4caf50', 0.15) : alpha('#ff9800', 0.15),
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        {isPositive ? (
          <TrendingUp sx={{ color: 'success.main', fontSize: '1rem', flexShrink: 0 }} />
        ) : (
          <TrendingDown sx={{ color: 'warning.main', fontSize: '1rem', flexShrink: 0 }} />
        )}
        <Typography 
          variant="body2" 
          sx={{ 
            flex: 1,
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.85rem',
            lineHeight: 1.2,
          }}
        >
          {formatSchoolName(school.school_name || school.name)}
        </Typography>
      </Box>
      <Typography 
        variant="caption" 
        sx={{ 
          color: 'text.secondary',
          fontSize: '0.7rem',
          display: 'block',
          ml: 3,
          lineHeight: 1.2,
        }}
      >
        {getDetails()}
      </Typography>
    </Box>
  );
};

// Section card component - compact version
const HighlightSection = ({ title, icon: Icon, schools, type, onSchoolClick, accentColor, tooltip }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        flex: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(accentColor, 0.25),
        bgcolor: alpha(accentColor, 0.08),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Icon sx={{ color: accentColor, fontSize: '1.1rem' }} />
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 700, 
            color: 'text.primary',
            fontSize: '0.9rem',
            flex: 1,
          }}
        >
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoOutlined sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* School list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
        {schools.map((school, index) => (
          <SchoolItem
            key={school.uuid || index}
            school={school}
            type={type}
            onClick={() => onSchoolClick(school)}
          />
        ))}
        {schools.length === 0 && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary', 
              fontStyle: 'italic',
              textAlign: 'center',
              py: 0.75,
              fontSize: '0.75rem',
            }}
          >
            No schools in this category
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const SchoolHighlights = () => {
  const navigate = useNavigate();
  const { districtData, loading, error } = useDistrictCache({ silentLoad: true });

  // Filter valid schools
  const validSchools = useMemo(() => {
    if (!districtData?.metrics) return [];
    return districtData.metrics.filter(
      school => school?.population && school?.enrollment && school?.market_share
    );
  }, [districtData]);

  // Calculate how many schools to show - max 5 for the new layout
  const schoolsToShow = useMemo(() => {
    const total = validSchools.length;
    if (total < 5) return 0; // Don't show section if less than 5 schools
    if (total < 10) return 3;
    if (total < 20) return 4;
    return 5; // Max 5 schools to fill the space nicely
  }, [validSchools.length]);

  // Identify bright spots - schools doing well
  const brightSpots = useMemo(() => {
    if (schoolsToShow === 0) return [];
    
    // Filter to schools that are growing in enrollment or gaining market share
    const candidates = validSchools.filter(school => {
      const enrollmentGrowing = school.enrollment?.status === 'growing';
      const projectionsGrowing = school.enrollment?.projection_status === 'growing';
      const marketShareGaining = school.market_share?.status === 'gaining';
      
      // Must have at least enrollment growing, plus one other positive indicator
      return enrollmentGrowing && (projectionsGrowing || marketShareGaining);
    });

    // Sort by enrollment trend (highest first)
    const sorted = candidates.sort((a, b) => {
      const aTrend = a.enrollment?.trend || 0;
      const bTrend = b.enrollment?.trend || 0;
      return bTrend - aTrend;
    });

    return sorted.slice(0, schoolsToShow);
  }, [validSchools, schoolsToShow]);

  // Identify schools to investigate - struggling schools
  const schoolsToInvestigate = useMemo(() => {
    if (schoolsToShow === 0) return [];
    
    // Filter to schools with declining enrollment or losing market share
    const candidates = validSchools.filter(school => {
      // Skip newer schools as they don't have enough data
      if (school.is_newer) return false;
      
      const enrollmentDeclining = school.enrollment?.status === 'declining';
      const projectionsDeclining = school.enrollment?.projection_status === 'declining';
      const marketShareLosing = school.market_share?.status === 'losing';
      
      // At least one negative indicator
      return enrollmentDeclining || projectionsDeclining || marketShareLosing;
    });

    // Sort by enrollment trend (lowest/most negative first)
    const sorted = candidates.sort((a, b) => {
      const aTrend = a.enrollment?.trend || 0;
      const bTrend = b.enrollment?.trend || 0;
      return aTrend - bTrend;
    });

    return sorted.slice(0, schoolsToShow);
  }, [validSchools, schoolsToShow]);

  const handleSchoolClick = (school) => {
    navigate(`/school-summary?school=${school.uuid}`);
  };

  // Don't render if not enough schools or no data
  if (loading && !districtData) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={100} height={18} />
                </Box>
                {[0, 1, 2].map((j) => (
                  <Skeleton key={j} variant="text" height={28} sx={{ mb: 0.25 }} />
                ))}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Don't show if less than 5 schools or error
  if (error || schoolsToShow === 0) {
    return null;
  }

  // Don't show if no schools in either category
  if (brightSpots.length === 0 && schoolsToInvestigate.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', gap: 2 }}>
      <HighlightSection
        title="Bright Spots"
        icon={StarOutline}
        schools={brightSpots}
        type="bright"
        onSchoolClick={handleSchoolClick}
        accentColor="#4caf50"
        tooltip="Schools identified as Bright Spots have growing enrollment plus at least one additional positive indicator (growing projections or gaining market share). They are sorted by enrollment trend, with the highest growth shown first."
      />
      <HighlightSection
        title="Schools to Investigate"
        icon={WarningAmberOutlined}
        schools={schoolsToInvestigate}
        type="investigate"
        onSchoolClick={handleSchoolClick}
        accentColor="#ff9800"
        tooltip="Schools identified for investigation have at least one concerning indicator: declining enrollment, declining projections, or losing market share. Newer schools are excluded due to insufficient data. They are sorted by enrollment trend, with the most negative trends shown first."
      />
    </Box>
  );
};

export default SchoolHighlights;

